/* eslint react/prefer-stateless-function: 0 */
/* eslint arrow-body-style: 0 */
import React, { PureComponent, Fragment } from 'react';
import classnames from 'classnames';
import Wahanda from 'common/wahanda';
import {
  Table,
  TableColumnHeader,
  TableRow,
  TableBody,
  TableHeader,
} from 'components/common/Table';
import { TransactionsReportTracking } from 'components/reports/transactions/tracking';
import { RedeemedVoucherLineItem } from 'components/reports/transactions/common/utils';
import style from './style.scss';
import Transaction from './Transaction';
import { TYPE, DISPLAY_TYPE, TransactionLineItem, TransactionPayment } from './Transaction/types';

const lang = {
  headings: Wahanda.lang.reports.transactions.table.headings,
  payments: Wahanda.lang.reports.transactions.table.payments,
  badges: Wahanda.lang.reports.transactions.table.badges,
};
interface TransactionRow {
  payments?: TransactionPayment[];
  lineItems?: TransactionLineItem[];
  redeemedVoucherLineItems?: RedeemedVoucherLineItem[];
  time: string;
  transactionId?: string;
  referenceTransactionId?: string;
  amount: number;
  description?: string;
  notes?: string;
  created: string;
  venueLocalDateTime?: string;
  customerName?: string;
  type: string;
  displayType?: string;
  venueTransactionType: string;
  totalTaxAmount?: number;
  checkoutId?: string;
  cancellationId?: string;
  venueTransactionId?: string | number;
  quoteId?: number;
  referenceOperationTime?: string;
}

interface ITransactionReportProps extends React.HTMLAttributes<Element> {
  transactions?: TransactionRow[];
  openDialog: (...args: any[]) => any;
  advancedView?: boolean;
}
class TransactionReport extends PureComponent<ITransactionReportProps, {}> {
  static defaultProps = {
    transactions: [],
    advancedView: false,
  };

  componentDidMount = () => {
    TransactionsReportTracking.trackTableView(this.props.advancedView);
  };

  isQuote = (transaction) => {
    return transaction.type === TYPE.QUOTE || transaction.type === TYPE.VOID;
  };

  renderSale = (transaction) => {
    const props = {
      advancedView: this.props.advancedView,
      onClick: this.props.openDialog,
      isSale: true,
      isWalkIn: !transaction.customerName,
      customerName: transaction.customerName || Wahanda.lang.shared.walkin,
      created: transaction.created,
      time: transaction.time,
      transactionId: transaction.transactionId,
      amount: transaction.amount,
      payments: transaction.payments,
      redeemedVoucherLineItems: transaction.redeemedVoucherLineItems,
      type: transaction.type,
      venueTransactionType: transaction.venueTransactionType,
      totalTaxAmount: transaction.totalTaxAmount,
      lineItems: transaction.lineItems,
      checkoutId: transaction.checkoutId,
      cancellationId: transaction.cancellationId,
      referenceTransactionId: transaction.referenceTransactionId,
    };
    return <Transaction {...props} key={transaction.transactionId} />;
  };

  renderTip = (transaction) => {
    const props = {
      advancedView: this.props.advancedView,
      onClick: this.props.openDialog,
      isTip: true,
      isWalkIn: !transaction.customerName,
      customerName: transaction.customerName || Wahanda.lang.shared.walkin,
      created: transaction.created,
      time: transaction.time,
      transactionId: transaction.transactionId,
      amount: transaction.amount,
      payments: transaction.payments,
      type: transaction.type,
      venueTransactionType: transaction.venueTransactionType,
      totalTaxAmount: transaction.totalTaxAmount,
      lineItems: transaction.lineItems,
      checkoutId: transaction.checkoutId,
      cancellationId: transaction.cancellationId,
      referenceTransactionId: transaction.referenceTransactionId,
    };
    return <Transaction {...props} key={`${transaction.transactionId}-tip`} />;
  };

  renderRefund = (transaction) => {
    const props = {
      showRefundNote: true,
      isRefund: true,
      advancedView: this.props.advancedView,
      onClick: this.props.openDialog,
      isWalkIn: !transaction.customerName,
      customerName: transaction.customerName || Wahanda.lang.shared.walkin,
      created: transaction.created,
      time: transaction.time,
      transactionId: transaction.transactionId,
      amount: transaction.amount,
      payments: transaction.payments,
      redeemedVoucherLineItems: transaction.redeemedVoucherLineItems,
      type: transaction.type,
      venueTransactionType: transaction.venueTransactionType,
      totalTaxAmount: transaction.totalTaxAmount,
      lineItems: transaction.lineItems,
      refundNote: transaction.notes,
      referenceTransactionId: transaction.referenceTransactionId,
      checkoutId: transaction.checkoutId,
      cancellationId: transaction.cancellationId,
    };
    return <Transaction {...props} key={transaction.cancellationId} />;
  };

  renderWithdrawal = (transaction) => {
    const props = {
      advancedView: this.props.advancedView,
      isWithdrawal: true,
      created: transaction.created,
      time: transaction.time,
      transactionId: transaction.transactionId,
      amount: transaction.amount,
      type: transaction.type,
      venueTransactionType: transaction.venueTransactionType,
      totalTaxAmount: transaction.totalTaxAmount,
      lineItems: transaction.lineItems,
      description: transaction.description,
      referenceTransactionId: transaction.referenceTransactionId,
      checkoutId: transaction.checkoutId,
      cancellationId: transaction.cancellationId,
    };
    return <Transaction {...props} key={transaction.venueTransactionId} />;
  };

  renderDeposit = (transaction) => {
    const props = {
      isDeposit: true,
      advancedView: this.props.advancedView,
      created: transaction.created,
      time: transaction.time,
      transactionId: transaction.transactionId,
      amount: transaction.amount,
      type: transaction.type,
      venueTransactionType: transaction.venueTransactionType,
      totalTaxAmount: transaction.totalTaxAmount,
      lineItems: transaction.lineItems,
      description: transaction.description,
      referenceTransactionId: transaction.referenceTransactionId,
      checkoutId: transaction.checkoutId,
      cancellationId: transaction.cancellationId,
    };
    return <Transaction {...props} key={transaction.venueTransactionId} />;
  };

  renderQuote = (transaction) => {
    const props = {
      advancedView: this.props.advancedView,
      isQuote: true,
      isWalkIn: !transaction.customerName,
      customerName: transaction.customerName,
      time: transaction.time,
      created: transaction.created,
      venueLocalDateTime: transaction.venueLocalDateTime,
      transactionId: transaction.transactionId,
      amount: transaction.amount,
      lineItems: transaction.lineItems,
      type: transaction.type,
      payments: transaction.payments,
      quoteId: transaction.quoteId,
      onClick: () => this.props.openDialog(transaction),
    };
    return <Transaction {...props} key={`${transaction.transactionId}-${transaction.created}`} />;
  };

  renderRow = (transaction: TransactionRow): JSX.Element | null => {
    if (this.isQuote(transaction)) {
      return this.renderQuote(transaction);
    }
    switch (transaction.displayType) {
      case DISPLAY_TYPE.SALE:
        return this.renderSale(transaction);
      case DISPLAY_TYPE.REFUND:
        return this.renderRefund(transaction);
      case DISPLAY_TYPE.TIP:
        return this.renderTip(transaction);
      case DISPLAY_TYPE.WITHDRAWAL:
        return this.renderWithdrawal(transaction);
      case DISPLAY_TYPE.DEPOSIT:
        return this.renderDeposit(transaction);
      default:
        return null;
    }
  };

  render() {
    const { transactions, advancedView } = this.props;
    return (
      <Table className={classnames(style.table, { [style.expandedView]: advancedView })}>
        <TableHeader>
          <TableRow>
            <TableColumnHeader className={classnames(style.timeHeader, style.noWrap)}>
              <div>{lang.headings.time}</div>
              <div className={style.transactionId}>{lang.headings.transactionHash}</div>
            </TableColumnHeader>
            <TableColumnHeader
              className={classnames(style.transactionHeader, style.noWrap, style.hideMobile)}
            >
              {lang.headings.transactionHash}
            </TableColumnHeader>
            <TableColumnHeader className={classnames(style.typeHeader)}>
              {lang.headings.transactionType}
            </TableColumnHeader>
            <TableColumnHeader className={style.clientHeader}>
              {lang.headings.client}
            </TableColumnHeader>
            <TableColumnHeader className={classnames(style.totalSumHeader, style.alignRight)}>
              {lang.headings.totalSum}
            </TableColumnHeader>
            {advancedView && (
              <Fragment>
                <TableColumnHeader className={classnames(style.notesHeader, style.dashedLeft)}>
                  {lang.headings.positionNotes}
                </TableColumnHeader>
                <TableColumnHeader
                  className={classnames(style.individSumHeader, style.minWidth, style.alignRight)}
                >
                  {lang.headings.individualSum}
                </TableColumnHeader>
                <TableColumnHeader
                  className={classnames(
                    style.vatPercentageHeader,
                    style.minWidth,
                    style.alignRight,
                  )}
                >
                  {lang.headings.vatPercentage}
                </TableColumnHeader>
                <TableColumnHeader
                  className={classnames(style.vatIndvHeader, style.minWidth, style.alignRight)}
                >
                  {lang.headings.vatIndividual}
                </TableColumnHeader>
              </Fragment>
            )}
            <TableColumnHeader
              className={classnames(
                style.paymentHeader,
                style.dashedLeft,
                style.payment,
                style.noWrap,
              )}
              colSpan="2"
            >
              <div className={style.paymentTitle}>{lang.headings.payment}</div>
              <div className={style.paymentSubheaders}>
                <span className={style.paymentInfo}>{lang.headings.type}</span>
                <span className={classnames(style.paymentInfo, style.alignRight)}>
                  {lang.headings.amount}
                </span>
              </div>
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>{transactions?.map(this.renderRow)}</TableBody>
      </Table>
    );
  }
}

export default TransactionReport;
